import LengthValue from '$src/CGS/domain/dto/core/Length/LengthValue';
import { LengthValueMillimeter } from '$src/CGS/domain/dto/core/Length/LengthValueMillimeter';
import * as THREE from 'three';
import { EMaterialSlot } from '../../Meshes/EMaterialSlot';
import { SubMeshCollection } from '../../Meshes/SubMeshCollection';
import { InsideContainerMetadata } from '../Metadata/InsideContainerMetadata';
import { InsideElementMetadata } from '../Metadata/InsideElementMetadata';
import { CabinetObject } from '../Objects/CabinetObject';
import { ICabinetShapeGenerator } from '../ShapeGenerators/ICabinetShapeGenerator';
import { InsideElementGeneratorFactory } from './InsideElementGeneratorFactory';
import { PolygonIndexer } from '../../PolygonIndexer';
import { PlankGenerator } from '../PlankGenerator';
import { LengthMaths } from '$src/CGS/domain/dto/core/Length/LengthMaths';
import { MeshInsideElementObject } from '../Objects/InsideElementObject';

export class InsideContainerGenerator {
    insideContainerMetadata: InsideContainerMetadata;
    cabinetShapeGenerator: ICabinetShapeGenerator;
    cabinet: CabinetObject;

    constructor(cabinet: CabinetObject, insideContainerMetadata: InsideContainerMetadata, cabinetShapeGenerator: ICabinetShapeGenerator) {
        this.cabinet = cabinet;
        this.insideContainerMetadata = insideContainerMetadata;
        this.cabinetShapeGenerator = cabinetShapeGenerator;
    }

    build(): void {
        for (const insideElementMetaData of this.insideContainerMetadata.Elements) {
            const insideElementGenerator = InsideElementGeneratorFactory.getGenerator(this, insideElementMetaData);
            insideElementGenerator?.build();
        }
        this.buildVerticalSeparationPlanks();
    }

    buildVerticalSeparationPlank(isEnd: boolean): void {
        const startFrontPlacement = this.insideContainerMetadata.StartPlacement;
        const endFrontPlacement = this.insideContainerMetadata.EndPlacement;

        const insideOutline = this.cabinetShapeGenerator.getInsideOutline(null, null);
        const startPointIndexer = new PolygonIndexer(insideOutline, this.cabinetShapeGenerator.getFrontFaceIndexInOutline(startFrontPlacement.FaceIndex, false));
        const endPointIndexer = new PolygonIndexer(insideOutline, this.cabinetShapeGenerator.getFrontFaceIndexInOutline(endFrontPlacement.FaceIndex, true));

        if (isEnd) {
            this.buildVerticalSeparationPlankObject(this.getEndVerticalPanel(endPointIndexer, endFrontPlacement.Offset));
        } else {
            this.buildVerticalSeparationPlankObject(this.getStartVerticalPanel(startPointIndexer, startFrontPlacement.Offset));
        }
    }

    buildVerticalSeparationPlankObject(outline: THREE.Vector3[]): void {
        const indexer = new PolygonIndexer(outline);
        if (indexer.getAllLengths().some(l => l.millimeter < 5) || indexer.total !== 4) return;

        const plankGenerator = new PlankGenerator(outline, LengthMaths.subtract(LengthMaths.getLengthValue(this.insideContainerMetadata.Top), LengthMaths.getLengthValue(this.insideContainerMetadata.VerticalPlankBottom)));
        const generatedObject = new THREE.Object3D();
        generatedObject.name = "SEPARATION_PLANK_GEO";

        generatedObject.position.set(0, LengthMaths.getLengthValue(this.insideContainerMetadata.VerticalPlankBottom).meter, 0);
        this.cabinet.Placement?.add(generatedObject);

        const mesh = new THREE.Mesh();
        generatedObject.add(mesh);

        const subMeshCollection = new SubMeshCollection();
        subMeshCollection.merge(plankGenerator.build(EMaterialSlot.Inside, EMaterialSlot.Front));

        const insideElementObject = new MeshInsideElementObject();
        insideElementObject.MeshInfo = subMeshCollection.createMesh(mesh);
        this.cabinet.InsideElements.push(insideElementObject);
    }

    buildVerticalSeparationPlanks(): void {
        const startFrontPlacement = this.insideContainerMetadata.StartPlacement;
        const endFrontPlacement = this.insideContainerMetadata.EndPlacement;
        if (!startFrontPlacement || !endFrontPlacement) return;

        this.buildVerticalSeparationPlank(true);
        this.buildVerticalSeparationPlank(false);
    }

    buildOutline(insideElementMetadata: InsideElementMetadata | null = null, minFrontPadding: LengthValue | null = null): THREE.Vector3[] {
        
        let frontEdgeToBoxDistance = insideElementMetadata?.FrontPadding || new LengthValueMillimeter(0);
        if (minFrontPadding && frontEdgeToBoxDistance < minFrontPadding) {
            frontEdgeToBoxDistance = new LengthValueMillimeter(minFrontPadding.millimeter);
        }

        const insideOutline = this.cabinetShapeGenerator.getInsideOutline(frontEdgeToBoxDistance, null);
        const startFrontPlacement = this.insideContainerMetadata.StartPlacement;
        const endFrontPlacement = this.insideContainerMetadata.EndPlacement;
        if (!startFrontPlacement || !endFrontPlacement) return insideOutline;

        const startPointIndexer = new PolygonIndexer(insideOutline, this.cabinetShapeGenerator.getFrontFaceIndexInOutline(startFrontPlacement.FaceIndex, false));
        const endPointIndexer = new PolygonIndexer(insideOutline, this.cabinetShapeGenerator.getFrontFaceIndexInOutline(endFrontPlacement.FaceIndex, true));

        return this.cutPolygon(startPointIndexer, endPointIndexer, startFrontPlacement.Offset, endFrontPlacement.Offset);
    }

    private cutPolygon(startPointIndexer: PolygonIndexer, endPointIndexer: PolygonIndexer, startOffset: LengthValue | null, endOffset: LengthValue | null): THREE.Vector3[] {
        const result: THREE.Vector3[] = [];
        const indexSentinel = new PolygonIndexer(startPointIndexer.polygon, startPointIndexer.currentIndex);

        const startPointTranslated = startPointIndexer.getCurrentVectorTranslated(startOffset);
        const endPointTranslated = endOffset ? endPointIndexer.getPreviousVectorTranslated(endOffset) : endPointIndexer.currentVector;

        result.push(startPointTranslated);
        indexSentinel.increment();

        while (indexSentinel.currentIndex !== endPointIndexer.currentIndex) {
            result.push(indexSentinel.currentVector);
            indexSentinel.increment();
        }

        const frontFaceDir = new THREE.Vector3().crossVectors(endPointIndexer.previousVectorNormalized, new THREE.Vector3(0, 1, 0));
        const perpendicularCutP2 = new THREE.Vector3().addVectors(endPointTranslated, frontFaceDir);

        result.push(endPointTranslated);
        indexSentinel.increment().increment();
        result.push(...indexSentinel.findIntersections(endPointTranslated, perpendicularCutP2, false, 1, false, true));

        const startFrontFaceDir = new THREE.Vector3().crossVectors(startPointIndexer.currentVectorNormalized, new THREE.Vector3(0, 1, 0));
        const startPerpendicularCutP2 = new THREE.Vector3().addVectors(startPointTranslated, startFrontFaceDir);

        result.push(...indexSentinel.findIntersections(startPointTranslated, startPerpendicularCutP2, true, 1, false, true));

        return result;
    }

    private getEndVerticalPanel(endPointIndexer: PolygonIndexer, endOffset: LengthValue | null): THREE.Vector3[] {
        const result: THREE.Vector3[] = [];
        const indexSentinel = new PolygonIndexer(endPointIndexer.polygon, endPointIndexer.currentIndex);

        const panelThickness = new LengthValueMillimeter(this.cabinetShapeGenerator.cabinet.Constants.BoxThickness.millimeter / 2);
        const panelOffset = endOffset ? LengthMaths.add(endOffset, panelThickness) : panelThickness;

        const endPointTranslated1 = endOffset ? endPointIndexer.getPreviousVectorTranslated(endOffset) : endPointIndexer.currentVector;
        const endPointTranslated2 = endOffset ? endPointIndexer.getPreviousVectorTranslated(panelOffset) : endPointIndexer.getCurrentVectorTranslated(panelThickness, true);

        const frontFaceDir = new THREE.Vector3().crossVectors(endPointIndexer.previousVectorNormalized, new THREE.Vector3(0, 1, 0));
        const perpendicularCutP2 = new THREE.Vector3().addVectors(endPointTranslated2, frontFaceDir);

        result.push(endPointTranslated1);
        result.push(...indexSentinel.findIntersections(endPointTranslated2, perpendicularCutP2, false, 2, false, true));

        const perpendicularCutP2End = new THREE.Vector3().addVectors(endPointTranslated1, frontFaceDir);
        result.push(indexSentinel.findIntersections(endPointTranslated1, perpendicularCutP2End, false, 1, false, true)[0]);

        return result;
    }

    private getStartVerticalPanel(startPointIndexer: PolygonIndexer, startOffset: LengthValue | null): THREE.Vector3[] {
        const result: THREE.Vector3[] = [];
        const indexSentinel = new PolygonIndexer(startPointIndexer.polygon, startPointIndexer.currentIndex);

        const panelThickness = new LengthValueMillimeter(-this.cabinetShapeGenerator.cabinet.Constants.BoxThickness.millimeter / 2);
        const panelOffset = startOffset ? LengthMaths.add(startOffset, panelThickness) : panelThickness;

        const startPointTranslated1 = startPointIndexer.getCurrentVectorTranslated(startOffset);
        const startPointTranslated2 = startPointIndexer.getCurrentVectorTranslated(panelOffset);

        const frontFaceDir = new THREE.Vector3().crossVectors(startPointIndexer.currentVectorNormalized, new THREE.Vector3(0, 1, 0));
        const perpendicularCutP2 = new THREE.Vector3().addVectors(startPointTranslated1, frontFaceDir);

        result.push(startPointTranslated1);
        result.push(indexSentinel.findIntersections(startPointTranslated1, perpendicularCutP2, false, 1, false, true)[0]);

        const perpendicularCutP2Start = new THREE.Vector3().addVectors(startPointTranslated2, frontFaceDir);
        result.push(...indexSentinel.findIntersections(startPointTranslated2, perpendicularCutP2Start, false, 2, false, true));

        return result;
    }
}
